import WideView from 'src/components/atomic/layouts/wideView/WideView';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import LiteralsService from 'src/services/literals/LiteralsService';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import Card from 'src/components/atomic/atoms/card/Card';
import styles from './TransactionDetailsView.module.scss';
import { useCategories } from 'src/hooks/queries/categories';
import { useTransaction } from 'src/hooks/queries/transactions';
import { useVisibleAccounts } from 'src/hooks/queries/accounts';
import { goToTransactionRules } from 'src/services/router.service';
import Loading from 'src/components/atomic/atoms/loading/Loading';
import { useDocumentTitleRaw } from 'src/hooks/useDocumentTitle';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import { useTrackPage } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import TransactionDetailsNameSection from 'src/components/atomic/molecules/transactionDetailsNameSection/TransactionDetailsNameSection';
import useCurrentUser from 'src/hooks/useCurrentUser';
import Typography from 'src/components/typography/Typography';
import AccountService from 'src/services/account.service';
import FormatService from 'src/services/format.service';
import { AccountModel } from 'src/types/account.model';
import TransactionDetailsBackAnchor from 'src/components/atomic/atoms/transactionDetailsBackAnchor/TransactionDetailsBackAnchor';
import Divider from 'src/components/atomic/atoms/divider/Divider';
import { IconSetting } from '@americanexpress/dls-react';
import TitleDetail from 'src/components/atomic/atoms/titleDetail/TitleDetail';
import { getTransactionTypeText } from 'src/utils/transactions.util';
import { BudgetPages } from 'src/components/atomic/pages/BudgetPages';
import { useUpdatingTransaction } from 'src/hooks/useUpdatingTransaction';
import TransactionTypeSkeletonLoader from 'src/components/atomic/atoms/transactionTypeSkeletonLoader/TransactionTypeSkeletonLoader';
import TransactionRulesWrapper from 'src/components/atomic/organisms/transactionRules/TransactionRules';
import TransactionsDetailsFormFeatureWrapper from 'src/components/atomic/organisms/transactionDetailsForm/TransactionsDetailsForm';

const Layout = ({ children }: { children: JSX.Element | JSX.Element[] }): JSX.Element => (
    <WideView
        withDocTitle={false}
        title={LiteralsService.getLiteral('TransactionDetails.title')}
        subtitle={LiteralsService.getLiteral('TransactionDetails.subtitle')}
        showFooter
        hasBackgroundBanner
        data-cy={Selectors.SpendSmart.DETAILS_VIEW}
        hideBackButton
    >
        <Card showBackgroundBanner>{children}</Card>
    </WideView>
);

export default function TransactionDetailsView(): JSX.Element {
    useDocumentTitleRaw(LiteralsService.getLiteral('Budget.titles.transactionDetails'));
    useTrackPage(LiteralsService.getLiteral('Budget.titles.transactionDetails'));
    const params = useParams();
    const history = useHistory();
    const { isLoading: isLoadingCategories } = useCategories();
    const { isLoading: isLoadingUser, isFullPlanUser } = useCurrentUser();
    const { isLoading: isLoadingTransaction, data: transaction } = useTransaction(Number(params.id));
    const { isLoading: isLoadingAccounts, data: accounts } = useVisibleAccounts();
    const { isUpdatingTransaction } = useUpdatingTransaction(Number(params.id), false);

    const isLoading: boolean = isLoadingCategories || isLoadingUser || isLoadingTransaction || isLoadingAccounts;
    const account: AccountModel = accounts?.find((account) => account?.id === transaction?.accountId);

    if (isLoading)
        return (
            <Layout>
                <Loading lg />
            </Layout>
        );

    return (
        <Layout>
            <TransactionDetailsBackAnchor />

            <Typography variant={TypographyVariants.BODY} component="p" data-cy={Selectors.Transactions.DATE}>
                {FormatService.formatDateFull(transaction?.date)}
            </Typography>

            <TitleDetail title={LiteralsService.getLiteral('TransactionDetails.transactionName')}>
                <TransactionDetailsNameSection />
            </TitleDetail>

            <TitleDetail title={LiteralsService.getLiteral('TransactionDetails.amount')}>
                <Typography variant={TypographyVariants.HEADING_L} component="p">
                    {FormatService.formatCurrency(Math.abs(transaction?.amount))}
                </Typography>
            </TitleDetail>

            <TitleDetail title={LiteralsService.getLiteral('TransactionDetails.merchantName')}>
                <Typography variant={TypographyVariants.BODY} component="p">
                    {transaction?.merchantName}
                </Typography>
            </TitleDetail>

            <TitleDetail title={LiteralsService.getLiteral('TransactionDetails.account')}>
                <Typography variant={TypographyVariants.BODY} component="p" className={styles.accountName}>
                    {AccountService.getDisplayName(account)}
                </Typography>
                <Typography variant={TypographyVariants.SUBTEXT} component="p" className={styles.colorTextWeak}>
                    {account?.institution.name}
                </Typography>
            </TitleDetail>

            <TitleDetail title={LiteralsService.getLiteral('TransactionDetails.type')}>
                {isUpdatingTransaction ? (
                    <TransactionTypeSkeletonLoader />
                ) : (
                    <Typography
                        variant={TypographyVariants.BODY}
                        component="p"
                        data-cy={Selectors.Transactions.TYPE_VALUE}
                    >
                        {getTransactionTypeText(transaction?.type)}
                    </Typography>
                )}
            </TitleDetail>

            <TransactionsDetailsFormFeatureWrapper />

            <Divider />

            <TransactionRulesWrapper />

            <Anchor
                data-cy={Selectors.Transactions.MANAGE_RULES}
                typographyProps={{
                    component: 'p',
                    variant: TypographyVariants.LINK,
                    className: styles.manageRulesLink
                }}
                onClick={() => goToTransactionRules(isFullPlanUser, history)}
                tag={adobeTags.Transactions.ManageRules}
                href={BudgetPages.TRANSACTIONS_RULES}
            >
                <IconSetting />
                {LiteralsService.getLiteral('TransactionsView.manageRules')}
            </Anchor>
        </Layout>
    );
}
